/**
 * useCamera — SimpleCameraModal preset
 *
 * Minimal CameraModal for basic photo capture — no ML edge detection,
 * no auto-capture, no auto-zoom, no review screen. Just a viewfinder
 * with capture, torch, zoom, and camera switching controls.
 *
 * Designed for evidence photos, odometer photos, messaging attachments,
 * leave request photos, and supporting documents.
 *
 * Usage:
 * ```tsx
 * const camera = useCamera({ onCapture: (b64) => ... });
 * return <SimpleCameraModal camera={camera} title="Take Photo" />;
 * ```
 */

import React from 'react';
import { CameraModal } from './CameraModal';
import type { UseCameraReturn, CaptureShortcut } from './types';

export interface SimpleCameraModalProps {
  /** useCamera hook return value */
  camera: UseCameraReturn;
  /** Title shown in header (default: 'Take Photo') */
  title?: string;
  /** Called after successful capture */
  onAfterCapture?: () => void;
  /** Custom capture button */
  captureButton?: React.ReactNode;
  /** Contextual quick-action shortcuts (optional) */
  shortcuts?: CaptureShortcut[];
  /** Enable hardware volume button to capture (default: true) */
  hardwareCapture?: boolean;
}

export function SimpleCameraModal({
  camera,
  title = 'Take Photo',
  onAfterCapture,
  captureButton,
  shortcuts,
  hardwareCapture,
}: SimpleCameraModalProps) {
  return (
    <CameraModal
      camera={camera}
      title={title}
      autoCapture={false}
      autoZoom={false}
      reviewCapture={false}
      cropToGuide={false}
      onAfterCapture={onAfterCapture}
      captureButton={captureButton}
      shortcuts={shortcuts}
      hardwareCapture={hardwareCapture}
    />
  );
}

export default SimpleCameraModal;
